import siteConfig from "site-config";
import { fetchJSON } from "lib/fetch";
import {
    useQuery,
    useMutation,
    queryCache,
    QueryConfig,
    MutateConfig,
    useInfiniteQuery,
    InfiniteQueryConfig,
} from "react-query";
import { getSubcategoryFilters } from "./helpers";
import { getBrand } from "containers/sites-manager/hooks/brands";
import { IAsset } from "constants/assets";
import merge from "lodash/merge";
import omit from "lodash/omit";
import React from "react";

export const PAGES = "pages";
export const SEARCH_SUGGESTIONS = "SEARCH_SUGGESTIONS";

export enum PageTypes {
    Site = "SITE",
    Brand = "BRAND",
    Category = "CATEGORY",
}

export interface IPage {
    site_id: string;
    page_id: string;
    kind: PageTypes;
    published: boolean;
    full_width: boolean;
    brand_id: string;
    title: string;
    body: { fields: object } | null;
    tags: string[];
    parents: string[];
    child_pages?: IPage[];
    created_by_user_id: string;
    last_modified_by_user_id: string;
    active: boolean;
    created_at: string;
    modified_at: string;
    hero_image: IAsset;
    tile_image: IAsset;
    description: string;
    weight: number;
    is_category: boolean;
}

export interface IPageSuggestion {
    site_id: string;
    page_id: string;
    kind: PageTypes;
    brand_id: string;
    title: string;
    tags: string[];
    type: string;
}

export enum PageSearchableField {
    Title = "TITLE",
    Tags = "TAGS",
    Description = "DESCRIPTION",
}

export enum PageSearchSortableField {
    SortByRelevance = "SORT_BY_RELEVANCE",
    SortByModifiedAtMs = "SORT_BY_MODIFIED_AT_MS",
    SortByCreatedAtMs = "SORT_BY_CREATED_AT_MS",
    SortByTitle = "SORT_BY_TITLE",
    SortByRandom = "SORT_BY_RANDOM",
}

export enum PageSearchSortOrder {
    Desc = "DESC",
    Asc = "ASC",
}

export enum PageFilterableField {
    PageId = "PAGE_ID",
    Kind = "KIND",
    BrandId = "BRAND_ID",
    PageTags = "PAGE_TAGS",
    Published = "PUBLISHED",
    Parents = "PARENTS",
    Active = "PAGE_ACTIVE",
}

export enum FilterKind {
    And = "and",
    Or = "or",
    Eq = "eq",
}

interface IEqualFilter {
    [FilterKind.Eq]: {
        field: PageFilterableField;
        value: string;
    };
}

interface IAndFilter {
    [FilterKind.And]: {
        filters: IPageFilter;
    };
}

interface IOrFilter {
    [FilterKind.Or]: {
        filters: IPageFilter;
    };
}

export type IPageFilter = Array<IAndFilter | IOrFilter | IEqualFilter>;

export interface ISearchPagesFilter {
    query: string;
    search_fields: PageSearchableField[];
    fields: PageSearchableField[];
    search_all_fields: boolean;
    search_after: string[];
    filters: IPageFilter;
    limit: number;
    sort_by: PageSearchSortableField;
    sort_order: PageSearchSortOrder;
    brand_id: string;
    with_scroll: boolean;
    scroll_id: string;
}

export interface ISearchSuggestionsFilter {
    query: string;
    fields: PageSearchableField[];
    brand_id: string;
    search_all_fields: boolean;
    limit: number;
    filters: Array<{
        field: PageFilterableField;
        value: string;
    }>;
}

export function buildCacheKeys(siteId: string, pageId?: string, filter?: Partial<ISearchPagesFilter>) {
    const compositeIds = { siteId, pageId: undefined, filter: undefined };

    if (pageId) {
        compositeIds.pageId = pageId;
    }

    if (filter && Object.keys(filter).length > 0) {
        compositeIds.filter = filter;
    }

    return [PAGES, compositeIds];
}

export const useSearchPages = (
    siteId: string,
    searchPagesFilter: Partial<ISearchPagesFilter>,
    options?: QueryConfig<IPage[]>
) => {
    return useQuery<IPage[]>(
        buildCacheKeys(siteId, undefined, searchPagesFilter),
        () =>
            fetchJSON<{ pages: IPage[] }>(`${siteConfig.goAPI}/v1/sites/${siteId}/pages_search`, {
                method: "POST",
                body: JSON.stringify(searchPagesFilter),
                noDomain: true,
            }).then((responseJson) => responseJson.pages),
        options
    );
};

export const useSearchPageSuggestions = (
    siteId: string,
    searchFilter: Partial<ISearchSuggestionsFilter>,
    options?: QueryConfig<IPageSuggestion[]>
) => {
    return useQuery<IPageSuggestion[]>(
        [SEARCH_SUGGESTIONS, searchFilter.query],
        () =>
            fetchJSON<{ data: IPageSuggestion[] }>(`${siteConfig.goAPI}/v1/sites/${siteId}/pages_search_suggestions`, {
                method: "POST",
                body: JSON.stringify(searchFilter),
                noDomain: true,
            }).then((responseJson) => responseJson.data),
        options
    );
};

export interface ISearchPagesResponse {
    search_after: string[];
    scroll_id: string;
    pages: IPage[];
}

export interface IFetchMoreOpts {
    search_after?: string[];
    scroll_id?: string;
}

export const useInfiniteSearchPages = (
    siteId: string,
    searchPagesFilter: Partial<ISearchPagesFilter>,
    options?: Partial<InfiniteQueryConfig<ISearchPagesResponse, string[]>>
) => {
    return useInfiniteQuery<ISearchPagesResponse>(
        buildCacheKeys(siteId, undefined, searchPagesFilter),
        (_pageKey, _filterKey, fetchMoreOpts: IFetchMoreOpts) => {
            return fetchJSON<ISearchPagesResponse>(`${siteConfig.goAPI}/v1/sites/${siteId}/pages_search`, {
                method: "POST",
                body: JSON.stringify({
                    ...searchPagesFilter,
                    ...fetchMoreOpts,
                }),
                noDomain: true,
            });
        },
        {
            ...options,
            getFetchMore: (lastPage) => {
                const fetchMoreOpts: IFetchMoreOpts = {};
                if (lastPage) {
                    const searchAfter = lastPage.search_after;
                    const scrollId = lastPage.scroll_id;
                    if (searchAfter.length) {
                        fetchMoreOpts.search_after = searchAfter;
                    }
                    if (scrollId) {
                        fetchMoreOpts.scroll_id = scrollId;
                    }
                }
                return Object.keys(fetchMoreOpts).length ? fetchMoreOpts : false;
            },
        }
    );
};

export const useCreatePages = (
    siteId: string,
    filter: Partial<ISearchPagesFilter>,
    mutationObject?: MutateConfig<IPage[], Error[]>
) => {
    const cacheKeys = buildCacheKeys(siteId, undefined, filter);
    return useMutation(createPages, {
        ...mutationObject,
        onSuccess: (data: IPage[]) => {
            queryCache.setQueryData(cacheKeys, (old: ISearchPagesResponse[]) => {
                const firstPage = old.shift();
                firstPage.pages = [...data.reverse(), ...firstPage.pages];

                return [firstPage, ...old];
            });
            mutationObject?.onSuccess?.(data, {});
        },
        onError: (error, variables, snap) => {
            queryCache.invalidateQueries(cacheKeys);
            mutationObject?.onError?.(error, variables, snap);
        },
    });
};

export const useCreatePage = (filter: Partial<ISearchPagesFilter>, onSuccess?: (data: IPage) => void) => {
    return useMutation(createPage, {
        onSuccess: (data: IPage) => {
            queryCache.setQueryData(buildCacheKeys(data.site_id, undefined, filter), (old: ISearchPagesResponse[]) => {
                if (old && data.parents.length === 0) {
                    if (old.length) {
                        const firstPage = old.shift();
                        firstPage.pages = [data, ...firstPage.pages];
                        return [{ ...firstPage }, ...old];
                    } else {
                        return [{ pages: [data] }];
                    }
                } else {
                    return old;
                }
            });
            onSuccess && onSuccess(data);
        },
        onError: (error) => {
            // left blank for now
        },
    });
};

export const useOptimisticPatchPage = (siteId: string, pageId: string, filter: Partial<ISearchPagesFilter> = {}) => {
    const cacheKeys = buildCacheKeys(siteId, pageId, filter);

    return useMutation(patchPage, {
        onMutate: ({ page, field_mask }: IPatchPageProps) => {
            const prevPage = queryCache.getQueryData(cacheKeys);
            const newData = getUpdatedPage(page);
            const updatedPage = merge(
                {},
                omit(prevPage as any, field_mask.paths?.length ? field_mask.paths : Object.keys(newData)),
                newData
            );
            queryCache.setQueryData(cacheKeys, updatedPage);

            return prevPage;
        },
        onError: (error, variables, snapshot) => {
            queryCache.setQueryData(cacheKeys, snapshot);
        },
    });
};

export const usePatchPage = (filter: Partial<ISearchPagesFilter> = {}) => {
    return useMutation(patchPage, {
        onSuccess: (data: IPage) => {
            queryCache.setQueryData(buildCacheKeys(data.site_id, data.page_id, filter), data);
        },
        throwOnError: true,
    });
};

export const usePatchPages = (filter: Partial<ISearchPagesFilter> = {}) => {
    return useMutation(patchPages, {
        onSuccess: (results: IPage[]) => {
            if (results.length) {
                queryCache.setQueryData(
                    buildCacheKeys(results[0].site_id, undefined, filter),
                    (old: ISearchPagesResponse[]) => {
                        results.forEach((data) => {
                            dance: for (const pageResult of old) {
                                for (const pageIndex in pageResult.pages) {
                                    if (data.page_id === pageResult.pages[pageIndex].page_id) {
                                        pageResult.pages[pageIndex] = data;
                                        pageResult.pages = [...pageResult.pages];
                                        break dance;
                                    }
                                }
                            }
                        });
                        return [...old];
                    }
                );
            }
        },
        onError: (error) => {
            // left blank for now
        },
    });
};

export const useDeletePage = (filter: Partial<ISearchPagesFilter> = {}) => {
    return useMutation(deletePage, {
        onSuccess: (_data, deletedPage: IPage) => {
            queryCache.setQueryData(
                buildCacheKeys(deletedPage.site_id, undefined, filter),
                (old: ISearchPagesResponse[]) => {
                    const updated = (old ?? []).map((searchPageResponse) => {
                        if (!deletedPage.parents.length) {
                            const updatedPages = searchPageResponse.pages.filter(
                                (p) => p.page_id !== deletedPage.page_id
                            );
                            return {
                                pages: updatedPages,
                                search_after: searchPageResponse.search_after,
                            };
                        } else {
                            return searchPageResponse;
                        }
                    });
                    return updated;
                }
            );
        },
        onError: (error) => {
            // left blank for now
        },
    });
};

export const useDeletePages = (
    siteId: string,
    filter: Partial<ISearchPagesFilter>,
    mutationObject?: MutateConfig<IPage[], Error[]>
) => {
    const cacheKeys = buildCacheKeys(siteId, undefined, filter);
    return useMutation(deletePages, {
        ...mutationObject,
        onSuccess: (data: IPage[]) => {
            queryCache.setQueryData(cacheKeys, (old: ISearchPagesResponse[]) => {
                const firstPage = old.shift();
                firstPage.pages = firstPage.pages.filter((it) => !data.some((x) => x.page_id === it.page_id));

                return [firstPage, ...old];
            });
            mutationObject?.onSuccess?.(data, {});
        },
        onError: (error, variables, snap) => {
            queryCache.invalidateQueries(cacheKeys);
            mutationObject?.onError?.(error, variables, snap);
        },
    });
};

export function useFetchPageById(siteId, pageId, options?: QueryConfig<IPage>) {
    return useQuery<IPage>(
        buildCacheKeys(siteId, pageId),
        () => {
            return fetchJSON<IPage>(`${siteConfig.goAPI}/v1/sites/${siteId}/pages/${pageId}`, {
                method: "GET",
                noDomain: true,
            });
        },
        options
    );
}

export const useFetchAllCategories = (siteId: string, brandId: string, enabled: boolean = true) => {
    const [categories, setCategories] = React.useState<ISearchPagesResponse[]>([]);
    const [categoriesFetchComplete, setCategoriesFetchComplete] = React.useState<boolean>(false);
    const { data, isFetching, isFetchingMore, fetchMore, canFetchMore } = useInfiniteSearchPages(
        siteId,
        {
            filters: [
                {
                    [FilterKind.Eq]: { field: PageFilterableField.Kind, value: PageTypes.Category },
                },
            ],
            sort_by: PageSearchSortableField.SortByTitle,
            sort_order: PageSearchSortOrder.Asc,
            brand_id: brandId,
        },
        { enabled }
    );

    React.useEffect(() => {
        if (canFetchMore && !isFetchingMore && !isFetching) {
            setTimeout(() => fetchMore(), 250);
        }
    }, [canFetchMore, isFetching, isFetchingMore, fetchMore, data]);

    React.useEffect(() => {
        if (!canFetchMore && !isFetchingMore && !isFetching && data) {
            setCategories(data);
            setCategoriesFetchComplete(true);
        }
    }, [canFetchMore, isFetching, isFetchingMore, data]);

    return { categories, categoriesFetchComplete };
};

function deletePages(pages: Array<Partial<IPage>>) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<Array<Partial<IPage>>>(async (resolve, reject) => {
        const errors: Error[] = [];
        const deletedPages: Partial<IPage>[] = [];
        for (const page of pages) {
            const { parents, child_pages } = page;
            try {
                if (page.kind !== PageTypes.Category || parents.length) {
                    deletedPages.push(page);
                    await deletePage(page);
                } else {
                    // ideally the subcategory deletion should be handle in the server as a cascade delete, but for now will do from the client
                    let childPages = child_pages;
                    if (!childPages?.length) {
                        const subcategoriesFilter = getSubcategoryFilters(page.page_id);
                        ({ pages: childPages } = await queryCache.prefetchQuery(
                            buildCacheKeys(page.site_id, undefined, subcategoriesFilter),
                            () => fetchSubcategories(page.site_id, subcategoriesFilter),
                            { force: true }
                        ));
                    }

                    const expandedPages = [page].concat(childPages || []);
                    for (const result of expandedPages) {
                        deletedPages.push(result);
                        await deletePage(result);
                    }
                }
            } catch (e) {
                errors.push(e);
            }
        }

        // log error to console for now
        if (errors.length) {
            // eslint-disable-next-line no-console
            errors.forEach((e: Error) => console.error(e?.message));
            reject(errors);
        } else {
            resolve(deletedPages);
        }
    });
}

function createPages(pages: Array<Partial<IPage>>) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<Array<Partial<IPage>>>(async (resolve, reject) => {
        const newPages: IPage[] = [];
        const errors: Error[] = [];
        for (let i = 0; i < pages.length; i++) {
            const page = pages[i];
            try {
                // If no title, look up the brand name
                if (page?.brand_id && !page.title) {
                    const brand = await getBrand(page.brand_id);
                    page.title = brand.name;
                }
                newPages.push(await createPage(page));
            } catch (e) {
                errors.push(e);
            }
        }

        // log error to console for now
        if (errors.length) {
            // eslint-disable-next-line no-console
            errors.forEach((e: Error) => console.error(e?.message));
            reject(errors);
        } else {
            resolve(newPages);
        }
    });
}

const sanitizePageTitle = (title: string = ""): string => title.trim();

function createPage(page: Partial<IPage>) {
    page.title = sanitizePageTitle(page.title);
    return fetchJSON<IPage>(`${siteConfig.goAPI}/v1/sites/${page.site_id}/pages`, {
        method: "POST",
        noDomain: true,
        body: JSON.stringify(page),
    });
}

const allowedPatchFields = [
    "body",
    "published",
    "title",
    "parents",
    "active",
    "tags",
    "hero_image",
    "tile_image",
    "description",
    "weight",
    "is_category",
    "full_width",
];

const getUpdatedPage = (page: Partial<IPage>): Partial<IPage> => {
    const updatedPage: Partial<IPage> = Object.keys(page).reduce((acc, key) => {
        if (allowedPatchFields.includes(key)) {
            acc[key] = page[key];
        }
        return acc;
    }, {});

    updatedPage.title = sanitizePageTitle(updatedPage.title);

    if (Object.keys(updatedPage).length === 0) {
        throw new Error("Page contains invalid properties.");
    }

    return updatedPage;
};

export interface IPatchPageProps {
    page: Partial<IPage>;
    field_mask?: { paths: string[] };
    brandId?: string;
}

function patchPage({ page, field_mask, brandId }: IPatchPageProps) {
    const updatedPage = getUpdatedPage(page);

    const payload = {
        page: updatedPage,
        field_mask: field_mask || { paths: Object.keys(updatedPage) },
        // It's likely `brandId` is empty string. Defaults to `undefined` so key won't be sent
        brand_id: brandId || undefined,
    };

    return fetchJSON<IPage>(`${siteConfig.goAPI}/v1/sites/${page.site_id}/pages/${page.page_id}`, {
        method: "PATCH",
        noDomain: true,
        body: JSON.stringify(payload),
    });
}

async function patchPages({
    pages,
    field_mask,
    brandId,
}: {
    pages: IPage[];
    field_mask: { paths: string[] };
    brandId?: string;
}) {
    const results = [];
    for (let i = 0; i < pages.length; i++) {
        const result = await patchPage({ page: pages[i], field_mask, brandId });

        results.push(result);
    }
    return results;
}

function deletePage(page: Partial<IPage>) {
    return fetchJSON<{}>(`${siteConfig.goAPI}/v1/sites/${page.site_id}/pages/${page.page_id}`, {
        method: "DELETE",
        noDomain: true,
    });
}

export const fetchSubcategories = (siteId, searchPagesFilter) => {
    return fetchJSON<{ pages: IPage[] }>(`${siteConfig.goAPI}/v1/sites/${siteId}/pages_search`, {
        method: "POST",
        body: JSON.stringify(searchPagesFilter),
        noDomain: true,
    });
};
